import * as React from 'react'

import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { SubmitButton } from '@/components/ui/submit-button'
import { AttachmentList } from '@/components/upload/AttachmentList'
import { AttachmentPicker } from '@/components/upload/AttachmentPicker'
import { useFileUploads } from '@/hooks/useFileUploads'
import { cn } from '@/lib/utils'
import type { DocumentType } from '@/models/document'

export interface RichTextareaSubmitPayload {
  message: string
  documentIds: string[]
}

type RichTextareaWithSubmitProps = {
  value?: string
  onValueChange?: (value: string) => void
  buttonProps?: React.ComponentProps<'button'>
  className?: string
  addonChildren?: React.ReactNode
  placeholder?: string
  onSubmitMessage?: (payload: RichTextareaSubmitPayload) => void
  editorKey?: number | string
  enableUploads?: boolean
  uploadDocumentType?: DocumentType
}

export function RichTextareaWithSubmit({
  className,
  buttonProps,
  onValueChange,
  addonChildren,
  value: controlledValue,
  placeholder,
  onSubmitMessage,
  editorKey,
  enableUploads,
  uploadDocumentType,
}: RichTextareaWithSubmitProps) {
  const [internalValue, setInternalValue] = React.useState('')
  const [submitTick, setSubmitTick] = React.useState(0)
  const value = controlledValue !== undefined ? controlledValue : internalValue
  const hasValue = value.trim().length > 0
  const uploads = useFileUploads({ documentType: uploadDocumentType })
  const uploadsEnabled = Boolean(enableUploads)
  const hasUploaded = uploadsEnabled && uploads.uploadedIds.length > 0
  const hasUploading = uploadsEnabled && uploads.hasUploading
  const canSubmit = (hasValue || hasUploaded) && !hasUploading
  const buttonDisabled = buttonProps?.disabled
  const isDisabled = !canSubmit || buttonDisabled
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const [isDragging, setIsDragging] = React.useState(false)

  function handleSubmit() {
    if (isDisabled) return
    const trimmed = value.trim()
    if (onSubmitMessage) {
      onSubmitMessage({
        message: trimmed,
        documentIds: uploadsEnabled ? uploads.uploadedIds : [],
      })
      if (uploadsEnabled) uploads.clearAll()
      setInternalValue('')
      setSubmitTick((t) => t + 1)
    } else {
      buttonProps?.onClick?.({} as React.MouseEvent<HTMLButtonElement>)
    }
  }

  return (
    <div
      className={cn(
        'border-input focus-within:border-ring bg-surface flex w-full flex-col overflow-hidden rounded-md border',
        uploadsEnabled && isDragging && 'ring-primary ring-2 ring-offset-2',
        className
      )}
      onDragEnter={(e) => {
        if (!uploadsEnabled) return
        e.preventDefault()
        setIsDragging(true)
      }}
      onDragOver={(e) => {
        if (!uploadsEnabled) return
        e.preventDefault()
      }}
      onDragLeave={(e) => {
        if (!uploadsEnabled) return
        if (e.currentTarget.contains(e.relatedTarget as Node)) return
        setIsDragging(false)
      }}
      onDrop={(e) => {
        if (!uploadsEnabled) return
        e.preventDefault()
        setIsDragging(false)
        const files = Array.from(e.dataTransfer.files || [])
        if (files.length > 0) void uploads.addFiles(files)
      }}
    >
      <MarkdownEditor
        key={`${editorKey ?? 0}-${submitTick}`}
        value={value}
        onChange={(v) => {
          setInternalValue(v)
          onValueChange?.(v)
        }}
        placeholder={placeholder}
        compact
        className="rounded-none! border-0!"
      />
      {uploadsEnabled && uploads.attachments.length > 0 && (
        <AttachmentList
          attachments={uploads.attachments}
          onRemove={(id) => void uploads.removeAttachment(id)}
        />
      )}
      <div className="flex items-center gap-2 px-3 py-2">
        {uploadsEnabled && (
          <AttachmentPicker
            onFiles={(files) => void uploads.addFiles(files)}
            disabled={buttonDisabled || hasUploading}
          />
        )}
        <div className="ml-auto flex items-center gap-2">
          {addonChildren}
          <SubmitButton
            ref={buttonRef}
            aria-label="Send"
            className={buttonProps?.className}
            disabled={isDisabled}
            {...buttonProps}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  )
}
